import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material'
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'
import { auth } from 'config/firebase'
import { authTextFieldStyle } from 'core/theme'
import { apiClient } from '@/core/api/apiClient'
import { useAuth } from 'hooks/auth/useAuth'
import { ClientOnboarding } from 'views/Client/ClientOnboarding/ClientOnboarding'
import { WebsiteApplyOnboarding } from './WebsiteApplyOnboarding'

const EMAIL_STORAGE_KEY = 'otw.applyEmail'
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const CHECK_DEBOUNCE_MS = 450

type Phase = 'email' | 'form' | 'done'

export const WebsiteApply = (): JSX.Element => {
  const navigate = useNavigate()
  const { currentUser, userInfo } = useAuth()
  const [formAnchorEl, setFormAnchorEl] = useState<HTMLDivElement | null>(null)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [existsMessage, setExistsMessage] = useState('')
  const [isChecking, setIsChecking] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState(false)
  const [phase, setPhase] = useState<Phase>('email')
  const [isLinkSigningIn, setIsLinkSigningIn] = useState(false)
  const [linkError, setLinkError] = useState('')
  const checkSeq = useRef(0)

  useEffect(() => {
    if (currentUser && userInfo?.role && !isSignInWithEmailLink(auth, window.location.href)) {
      navigate(`/${userInfo.role === 'babyAdmin' ? 'admin' : userInfo.role}`, { replace: true })
    }
  }, [currentUser, userInfo, navigate])

  useEffect(() => {
    const href = window.location.href
    if (!isSignInWithEmailLink(auth, href)) {
      return
    }

    const params = new URLSearchParams(window.location.search)
    const stored = window.sessionStorage.getItem(EMAIL_STORAGE_KEY)
    const linkEmail = params.get('email') || stored
    if (!linkEmail) {
      setLinkError(
        'Open the link from the same browser you used to request it, or enter your email again on the sign-in page.',
      )
      return
    }

    setIsLinkSigningIn(true)
    void signInWithEmailLink(auth, linkEmail, href)
      .then(() => {
        window.sessionStorage.removeItem(EMAIL_STORAGE_KEY)
        navigate('/needs-reset-password', { replace: true })
      })
      .catch(() => {
        setLinkError('That sign-in link is invalid or expired. Request a new one from support or try signing in.')
      })
      .finally(() => {
        setIsLinkSigningIn(false)
      })
  }, [navigate])

  useEffect(() => {
    const trimmed = email.trim().toLowerCase()
    setExistsMessage('')
    setEmailAvailable(false)
    setEmailError('')

    if (!trimmed) {
      setIsChecking(false)
      return
    }

    if (!EMAIL_RE.test(trimmed)) {
      setIsChecking(false)
      return
    }

    const seq = ++checkSeq.current
    setIsChecking(true)
    const timer = window.setTimeout(() => {
      void apiClient.portal.public
        .checkWebsiteApplyEmail(trimmed)
        .then(result => {
          if (seq !== checkSeq.current) return
          if (result.exists) {
            setExistsMessage('An account with this email already exists. Please sign in to continue.')
            setEmailAvailable(false)
          } else {
            setExistsMessage('')
            setEmailAvailable(true)
          }
        })
        .catch(() => {
          if (seq !== checkSeq.current) return
          setEmailError('Could not verify email. Try again.')
          setEmailAvailable(false)
        })
        .finally(() => {
          if (seq === checkSeq.current) setIsChecking(false)
        })
    }, CHECK_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [email])

  const handleContinue = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = email.trim().toLowerCase()
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError('Enter a valid email address')
      return
    }
    if (!emailAvailable || isChecking) {
      return
    }
    window.sessionStorage.setItem(EMAIL_STORAGE_KEY, trimmed)
    setPhase('form')
  }

  if (isLinkSigningIn) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={32} />
        <Typography variant='body2' sx={{ mt: 2 }}>
          Signing you in…
        </Typography>
      </Box>
    )
  }

  if (phase === 'done') {
    return (
      <Box sx={{ width: '100%', maxWidth: 560 }}>
        <Typography variant='h5' component='h1' gutterBottom>
          Application submitted
        </Typography>
        <Typography variant='body1' sx={{ mb: 2 }}>
          We created your portal account and sent a sign-in link to{' '}
          <strong>{email.trim().toLowerCase()}</strong>. Open that email to access the portal — you will set a
          password on first sign-in.
        </Typography>
        <Button variant='contained' onClick={() => navigate('/login')} sx={{ mt: 1 }}>
          Go to sign in
        </Button>
      </Box>
    )
  }

  if (phase === 'form') {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Applying as <strong>{email.trim().toLowerCase()}</strong>
          {' · '}
          <Link
            component='button'
            type='button'
            variant='body2'
            onClick={() => setPhase('email')}
            sx={{ verticalAlign: 'baseline' }}
          >
            Change email
          </Link>
        </Typography>
        <ClientOnboarding
          publicApplyEmail={email.trim().toLowerCase()}
          onPublicSubmitSuccess={() => setPhase('done')}
        />
      </Box>
    )
  }

  return (
    <Box
      ref={setFormAnchorEl}
      component='form'
      onSubmit={handleContinue}
      data-feature-demo-anchor='website-apply'
      sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: theme => theme.zIndex.modal + 1 }}
    >
      <WebsiteApplyOnboarding anchorEl={formAnchorEl} />
      <Typography variant='h5' component='h1' gutterBottom>
        Apply for financing
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Enter your email to start. If you already have a portal account, we will ask you to sign in instead.
      </Typography>
      {linkError ? (
        <Alert severity='error' sx={{ mb: 2 }}>
          {linkError}
        </Alert>
      ) : null}
      {existsMessage ? (
        <Alert severity='info' sx={{ mb: 2 }}>
          {existsMessage}{' '}
          <Link component='button' type='button' onClick={() => navigate('/login')} sx={{ fontWeight: 600 }}>
            Sign in
          </Link>
        </Alert>
      ) : null}
      <TextField
        required
        fullWidth
        autoFocus
        margin='normal'
        id='email'
        label='Email Address'
        name='email'
        value={email}
        onChange={event => setEmail(event.target.value)}
        error={!!emailError}
        helperText={emailError || (isChecking ? 'Checking…' : emailAvailable ? 'Email is available' : ' ')}
        autoComplete='email'
        sx={authTextFieldStyle}
        InputProps={{
          endAdornment: isChecking ? <CircularProgress size={18} /> : undefined,
        }}
      />
      <Button
        type='submit'
        fullWidth
        variant='contained'
        disabled={!emailAvailable || isChecking}
        sx={{ mt: 2, mb: 2 }}
      >
        Continue to application
      </Button>
      <Link component='button' type='button' variant='body2' onClick={() => navigate('/login')} sx={{ color: 'black' }}>
        Already have an account? Sign in
      </Link>
    </Box>
  )
}

export default WebsiteApply
